// Which teams' media cookies this tab holds (spec assets/media-cookies, Design 4). `client.ts`
// reads it to send `Prefer: media=cookie`, and `platform/media/access.ts`, which asks for the
// cookies, writes it. A grant ends `expires_in` seconds after its answer arrived, on this tab's
// clock, never at the server's `expires_at` read against it: a browser whose clock is off still
// stops asking in time.

/** A team's request asks for bare media URLs only while its cookie has this long left. */
export const HEADER_MARGIN_MS = 5 * 60_000;

/** The `Prefer` value `client.ts` sends with a team's request while its cookie is fresh. */
export const MEDIA_PREFERENCE = "media=cookie";

const TEAM_PATH = /^\/api\/v1\/teams\/([^/]+)\//;

// Each team's grant (by lower-case id): when it ends, in `Date.now()` milliseconds. A sign-in or
// a sign-out resets both, so a grant never outlives the session that asked for it.
const grants = new Map<string, number>();
// Teams whose bare URLs failed again after a fresh cookie: signed for the rest of this tab's life.
const dropped = new Set<string>();

function id(teamId: string): string {
  return teamId.toLowerCase();
}

/** This tab now holds `teamId`'s media cookie until `endsAt` (`Date.now()` milliseconds). */
export function holdMediaCookie(teamId: string, endsAt: number): void {
  if (!dropped.has(id(teamId))) grants.set(id(teamId), endsAt);
}

/** This tab holds no media cookie for `teamId` (cookies off, or a refused renewal). */
export function forgetMediaCookie(teamId: string): void {
  grants.delete(id(teamId));
}

/** `teamId` reads signed URLs for the rest of this tab's life, whatever it is granted later. */
export function dropMediaCookie(teamId: string): void {
  dropped.add(id(teamId));
  grants.delete(id(teamId));
}

/** Whether `teamId` reads signed URLs for the rest of this tab's life. */
export function mediaCookieDropped(teamId: string): boolean {
  return dropped.has(id(teamId));
}

/** How long `teamId`'s cookie has left, in milliseconds: 0 with none. */
export function mediaCookieLeft(teamId: string, now: number = Date.now()): number {
  return Math.max(0, (grants.get(id(teamId)) ?? now) - now);
}

/** Whether a request to `path` asks for bare media URLs: a team's, with its cookie fresh. */
export function prefersBareMedia(path: string, now: number = Date.now()): boolean {
  const team = TEAM_PATH.exec(path)?.[1];
  return team !== undefined && mediaCookieLeft(team, now) >= HEADER_MARGIN_MS;
}

/** Forgets every team's grant and drop: a sign-in or a sign-out, and between tests. */
export function resetMediaCookies(): void {
  grants.clear();
  dropped.clear();
}

/**
 * Whether `url` is a file's own URL, as an answer to `Prefer: media=cookie` gives it: http(s)
 * with no query (a signed URL carries its signature in the query). A read of it must carry the
 * media cookie, so it goes with credentials; a signed one goes without, as before the cookies
 * (the CDN answers a credentialed read only once its `Access-Control-Allow-Credentials` is on,
 * which comes with them).
 */
export function isBareMediaUrl(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.href);
    return (parsed.protocol === "https:" || parsed.protocol === "http:") && parsed.search === "";
  } catch {
    return false;
  }
}

/** The `crossOrigin` of an element that reads `url`'s pixels (a canvas): see `isBareMediaUrl`. */
export function mediaCrossOrigin(url: string): "use-credentials" | "anonymous" {
  return isBareMediaUrl(url) ? "use-credentials" : "anonymous";
}

/** The `credentials` of a `fetch` of the file at `url` (a download): see `isBareMediaUrl`. */
export function mediaCredentials(url: string): RequestCredentials {
  return isBareMediaUrl(url) ? "include" : "omit";
}
